import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';
import Breadcrumbs from '../../components/common/Breadcrumbs';
import RelatedGuides from '../../components/common/RelatedGuides';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const dayColors = [
  'from-teal-500 to-emerald-600',
  'from-emerald-500 to-green-600',
  'from-amber-500 to-orange-600',
  'from-rose-400 to-pink-500',
  'from-cyan-500 to-teal-600',
  'from-orange-500 to-red-500',
  'from-lime-500 to-green-600',
  'from-teal-400 to-cyan-500',
  'from-amber-400 to-yellow-500',
  'from-emerald-400 to-teal-500',
  'from-rose-500 to-red-500',
  'from-cyan-400 to-teal-500',
  'from-orange-400 to-amber-500',
  'from-teal-500 to-green-600',
];

const dayIcons = [
  'ri-plane-line',
  'ri-walk-line',
  'ri-tent-line',
  'ri-rest-time-line',
  'ri-ship-line',
  'ri-fire-line',
  'ri-cup-line',
  'ri-ancient-gate-line',
  'ri-store-3-line',
  'ri-translate-2-line', // fixed typo – added missing “-line”
  'ri-landscape-line',
  'ri-sun-line',
  'ri-shopping-bag-line',
  'ri-flight-takeoff-line',
];

interface DayData {
  day: number;
  title: string;
  subtitle: string;
  morning: string;
  afternoon: string;
  evening: string;
  logistics: string;
  cost: string;
  alternative: string;
  proTip?: string;
  link?: { text: string; to: string };
}

const itineraryDays: DayData[] = [
  {
    day: 1,
    title: 'Arrival & Antigua Orientation',
    subtitle: 'Settle in and explore the colonial heart',
    morning:
      'Arrive at Guatemala City Airport, take shuttle to Antigua (book 10am-2pm arrival). Check into accommodation, grab lunch near Parque Central.',
    afternoon:
      'Walking orientation tour. Visit Parque Central, Arco de Santa Catalina, La Merced Church. Get SIM card at Tigo or Claro store ($5-10 for 2 weeks data).',
    evening:
      'Dinner at Café Sky (rooftop views) or Hector’s Bistro. Early night to adjust.',
    logistics: 'Airport shuttle booked in advance. No tours needed today.',
    cost: '$50-80 (shuttle, meals, SIM card)',
    alternative: 'Same plan works indoors if rainy.',
  },
  {
    day: 2,
    title: 'Colonial Antigua Deep Dive',
    subtitle: 'Churches, ruins, and local life',
    morning:
      'Breakfast at Café Condesa. Visit San Francisco Church, Convento de las Capuchinas ruins ($5 entry). Climb to rooftop for volcano views.',
    afternoon:
      'Lunch at Café Mediterráneo. Walk to Cerro de la Cruz viewpoint (30 min uphill, go with a group or guide). Panoramic city + volcano views.',
    evening:
      'Sunset at rooftop bar. Dinner at Tartines or Sobremesa. Stroll Parque Central at night (well-lit, safe).',
    logistics: 'Cerro de la Cruz: go 9am-4pm with others. Entry free.',
    cost: '$40-70 (meals, entrance fees)',
    alternative: 'Skip Cerro if rainy; visit ChocoMuseo for chocolate workshop instead.',
    proTip: 'Cerro de la Cruz is safe during the day but avoid going alone. Join a free walking tour group.',
  },
  {
    day: 3,
    title: 'Acatenango Volcano Overnight (Day 1)',
    subtitle: 'Start the epic 2-day hike',
    morning:
      'Pick-up 8-9am. Drive 1.5 hrs to trailhead. Start hiking through cloud forest. Lunch break halfway (packed lunch included).',
    afternoon:
      'Continue steep ascent to base camp (3,700 m). Arrive 4-5pm. Set up tents, hot drinks provided.',
    evening:
      'Watch Fuego volcano erupt every 15-30 min. Dinner around campfire. Sleep in sleeping bag (provided). Temps drop to 0°C.',
    logistics: 'Book with Tropicana, Wicho & Charlie, or Old Town Outfitters ($35-50). Includes transport, meals, gear.',
    cost: '$35-50 (tour all-inclusive)',
    alternative: 'If weather is bad, reschedule. Operators allow free changes.',
    proTip: 'Bring: warm layers, headlamp, snacks, 3L water, altitude meds. Rent extra sleeping bag if cold-sensitive.',
    link: { text: 'Full Acatenango guide', to: '/acatenango-hike-from-antigua' },
  },
  {
    day: 4,
    title: 'Acatenango Volcano Overnight (Day 2)',
    subtitle: 'Summit sunrise and descent',
    morning:
      '4am wake-up. Summit push in the dark (1 hr to 3,976 m). Watch sunrise over Guatemala. Descend to base camp for breakfast.',
    afternoon:
      'Hike down to trailhead (3-4 hrs). Return to Antigua 2-3pm. Shower, nap, recover.',
    evening:
      'Light dinner. Early sleep. Your legs will thank you.',
    logistics: 'Same tour continues. Return transport included.',
    cost: 'Included in Day 3 tour price',
    alternative: 'Some skip summit if too tired. Still worth the overnight for Fuego views.',
  },
  {
    day: 5,
    title: 'Rest & Recovery Day',
    subtitle: 'Gentle activities after the volcano',
    morning:
      'Sleep in. Breakfast at Fernando\'s Kaffee. Visit Jade Museum or Textile Museum ($3-5 entry).',
    afternoon:
      'Spa treatment or massage ($25-50). Lunch at Panza Verde. Browse artisan markets on 4a Calle.',
    evening:
      'Casual dinner. Catch up on photos and journaling.',
    logistics: 'No bookings needed. Walk everywhere.',
    cost: '$50-100 (spa, meals, shopping)',
    alternative: 'Same plan works rain or shine.',
    proTip: 'Book a massage at Mayan Spa or Antigua Spa Resort. Your muscles need it after Acatenango.',
  },
  {
    day: 6,
    title: 'Lake Atitlán Day Trip',
    subtitle: 'Three volcanoes, indigenous villages',
    morning:
      'Pick-up 7am. Drive 2.5 hrs to Panajachel. Boat to Santiago Atitlán (Tz\'utujil village). Visit church, market, weaving co-ops.',
    afternoon:
      'Boat to San Juan La Laguna. Art galleries, coffee co-op tour, lunch with lake views.',
    evening:
      'Return to Panajachel. Drive back to Antigua (arrive 6-7pm). Dinner in town.',
    logistics: 'Book tour with Atitrans or local agency ($40-70 includes transport, boat, guide).',
    cost: '$40-70 (tour) + $15-25 (meals, tips)',
    alternative: 'If rainy, lake is still beautiful. Bring rain jacket.',
    proTip: 'Bring cash for artisan purchases. Prices are lower than Antigua and support local weavers directly.',
    link: { text: 'Lake Atitlán guide', to: '/day-trips-from-antigua-guatemala' },
  },
  {
    day: 7,
    title: 'Coffee Farm Tour',
    subtitle: 'Bean to cup experience',
    morning:
      'Pick-up 8:30am. Visit Finca Filadelfia or Finca La Azotea (30 min from Antigua). Walk through coffee plants, processing stations.',
    afternoon:
      'Roasting demo, cupping session. Lunch at finca. Return to Antigua 2pm.',
    evening:
      'Free evening. Explore 5a Avenida shops. Dinner at Café No Sé or La Fonda de la Calle Real.',
    logistics: 'Book tour through hotel or agency ($25-40 includes transport, guide, tasting).',
    cost: '$25-40 (tour) + $20-30 (meals)',
    alternative: 'If rainy, tour still runs (mostly covered areas).',
  },
  {
    day: 8,
    title: 'Pacaya Volcano Day Trip',
    subtitle: 'Active lava flows and roasted marshmallows',
    morning:
      'Pick-up 6am. Drive 1.5 hrs to Pacaya National Park. Start hike through lava fields (2-3 hrs round trip, moderate difficulty).',
    afternoon:
      'Reach active lava flows. Roast marshmallows on hot rocks. Descend. Return to Antigua 2-3pm.',
    evening:
      'Lunch in town. Afternoon siesta. Evening at leisure.',
    logistics: 'Book with any agency ($25-35 includes transport, guide, park fee). Bring sturdy shoes.',
    cost: '$25-35 (tour) + $15-20 (meals)',
    alternative: 'If Pacaya is too active (rare), tour gets rescheduled.',
    proTip: 'Pacaya is easier than Acatenango but still requires good shoes. Lava rocks are sharp.',
    link: { text: 'Pacaya guide', to: '/day-trips-from-antigua-guatemala' },
  },
  {
    day: 9,
    title: 'Chichicastenango Market (Thursday or Sunday)',
    subtitle: "Guatemala's largest indigenous market",
    morning:
      'Pick-up 6:30am. Drive 2.5 hrs to "Chichi". Explore massive market: textiles, masks, pottery, food. Visit Santo Tomás Church (Mayan ceremonies inside).',
    afternoon:
      'Lunch at market stalls. More shopping. Visit Pascual Abaj shrine (hilltop Mayan altar).',
    evening:
      'Return to Antigua (arrive 6pm). Dinner in town.',
    logistics: 'Book tour ($50-80 includes transport, guide). Market only Thu/Sun. Check calendar.',
    cost: '$50-80 (tour) + $30-50 (shopping, meals)',
    alternative: 'If not Thu/Sun, swap with Day 10 (Hobbitenango).',
    proTip: 'Bargain respectfully. Start at 50-60% of asking price. Bring small bills.',
  },
  {
    day: 10,
    title: 'Hobbitenango & Relaxation',
    subtitle: 'Whimsical photo spot and nature',
    morning:
      'Tuk-tuk or Uber to Hobbitenango (30 min, $10-15). Explore hobbit houses, giant hand sculpture, zip-lines, hiking trails.',
    afternoon:
      'Lunch at on-site restaurant (good views). More photos. Return to Antigua 3pm.',
    evening:
      'Sunset at Café Sky. Dinner at Rincon Tipico.',
    logistics: 'Entry $7. Zip-line extra $10. Open 9am-6pm daily.',
    cost: '$30-50 (transport, entry, meals)',
    alternative: 'If rainy, Hobbitenango is muddy. Swap with museum day.',
  },
  {
    day: 11,
    title: 'Cooking Class & Market Tour',
    subtitle: 'Learn to make Guatemalan dishes',
    morning:
      'Meet at local market 9am. Shop for ingredients with chef. Walk to cooking school.',
    afternoon:
      'Hands-on class: make pepián, tortillas, tamales. Eat your creations for lunch.',
    evening:
      'Free evening. Digest. Light dinner if hungry.',
    logistics: 'Book with Antigua Cooking School or similar ($40-60 per person, 4 hrs).',
    cost: '$40-60 (class includes lunch)',
    alternative: 'Swap with Spanish lesson or salsa class.',
    proTip: 'Ask for vegetarian or vegan options when booking. Most schools accommodate.',
  },
  {
    day: 12,
    title: 'Iximché Ruins & Comalapa',
    subtitle: 'Mayan archaeology and indigenous art',
    morning:
      'Pick-up 8am. Drive 1.5 hrs to Iximché ruins (former Kaqchikel capital). Guided tour of pyramids, ball courts, ceremonial sites.',
    afternoon:
      'Drive to San Juan Comalapa (indigenous art village). Visit galleries, meet local painters. Lunch.',
    evening:
      'Return to Antigua 5pm. Dinner at Nokiate or Fusion.',
    logistics: 'Book tour ($30-50 includes transport, guide, entry).',
    cost: '$30-50 (tour) + $20-30 (meals)',
    alternative: 'If rainy, ruins are slippery. Wear good shoes or reschedule.',
  },
  {
    day: 13,
    title: 'Free Day: Your Choice',
    subtitle: 'Repeat a favorite or try something new',
    morning:
      'Options: Revisit Lake Atitlán (stay overnight this time), Monterrico beach day trip, mountain biking, Spanish lesson, yoga class.',
    afternoon:
      'Continue chosen activity or relax in Antigua.',
    evening:
      'Farewell dinner at Mesón Panza Verde or Angeline.',
    logistics: 'Book based on preference. Most activities $30-80.',
    cost: '$30-100 depending on choice',
    alternative: 'Use this day as buffer if weather affected earlier plans.',
    proTip: 'If you loved Lake Atitlán, consider staying 2 nights there instead of day-tripping twice.',
  },
  {
    day: 14,
    title: 'Departure Day',
    subtitle: 'Last-minute shopping and goodbye',
    morning:
      'Sleep in. Breakfast at favorite café. Last-minute souvenir shopping (jade, textiles, coffee).',
    afternoon:
      'Check out. Shuttle to Guatemala City Airport (book 3-4 hrs before flight).',
    evening:
      'Depart Guatemala. Adiós!',
    logistics: 'Pre-book return shuttle ($12-15). Allow 1.5 hrs travel time + 2 hrs airport buffer.',
    cost: '$15-30 (shuttle, snacks)',
    alternative: 'If early flight, stay near airport night before (not recommended unless necessary).',
  },
];

// Add FAQ data
const faqData = [
  {
    q: 'Is 2 weeks too long to spend in Antigua Guatemala?',
    a: 'Not at all. Two weeks gives you time to explore Antigua deeply, do multiple volcano hikes (Acatenango, Pacaya), take day trips to Lake Atitlán and Chichicastenango, attend a cooking class, visit coffee farms, and still have rest days. Many travelers find 2 weeks in Antigua Guatemala the perfect balance between depth and relaxation.',
  },
  {
    q: 'What is the best time of year for a 2 week Antigua itinerary?',
    a: 'January through March is ideal: dry season, warm days (70-75°F), cool nights, and clear volcano views. November-December and April are also good. May-October is rainy season (afternoon showers) but fewer tourists and lower prices. Avoid Holy Week (Semana Santa, late March/early April) unless you want massive crowds.',
  },
  {
    q: 'How much does 2 weeks in Antigua Guatemala cost?',
    a: 'Budget travelers: $720-$1,180 total ($50-85/day). Mid-range: $1,580-$2,630 ($110-190/day). Comfort: $3,100-$5,520 ($220-395/day). This includes accommodation, food, activities, and local transport. Flights not included.',
  },
  {
    q: 'Can I do Acatenango and Pacaya in the same week?',
    a: 'Yes, but space them out. Do Acatenango first (harder, 2 days), rest 2-3 days, then Pacaya (easier, half day). Your legs need recovery time. This itinerary schedules them on Days 3-4 and Day 8.',
  },
  {
    q: 'Is Antigua safe for 2 weeks as a solo traveler?',
    a: 'Yes. Antigua is one of Guatemala\'s safest cities. Tourist police (POLITUR) patrol daily. Follow standard precautions: don\'t walk alone after dark, use ATMs inside banks, don\'t flash valuables. Join group tours for volcano hikes. Thousands of solo travelers visit annually without issues.',
  },
  {
    q: 'Do I need to speak Spanish for 2 weeks in Antigua?',
    a: 'Not required but helpful. Antigua is tourist-friendly; many locals speak basic English. Outside Antigua (markets, villages), Spanish is essential. Consider taking a few Spanish lessons during your stay ($6-10/hour for private lessons).',
  },
  {
    q: 'Should I book tours in advance or in Antigua?',
    a: 'Book airport shuttle in advance. Book everything else 1-2 days ahead in Antigua for flexibility (weather changes). Exception: Chichicastenango only runs Thu/Sun, so plan accordingly. Acatenango tours fill up in high season; book 2-3 days ahead.',
  },
  {
    q: 'Can I extend this itinerary to 3 weeks?',
    a: 'Absolutely. Add 3-5 days in Lake Atitlán (village hopping, kayaking, hiking), 3-4 days in Flores/Tikal (Mayan ruins, jungle), or 2-3 days in Semuc Champey (turquoise pools, caves). Or simply slow down the pace and add more rest days.',
  },
  {
    q: 'What should I pack for 2 weeks in Antigua?',
    a: 'Layers (T-shirts, fleece, rain jacket), hiking boots + walking shoes, daypack, headlamp, sunscreen, insect repellent, reusable water bottle, altitude meds (Diamox), basic first-aid kit, swimsuit, hat, sunglasses. Pack 7 days of clothes; laundry costs $3-5.',
  },
  {
    q: 'Is altitude sickness a problem in Antigua?',
    a: 'Antigua sits at 1,500 m (4,900 ft), which rarely causes issues. However, Acatenango summit is 3,976 m (13,045 ft). Spend 2-3 days in Antigua before hiking to acclimatize. Drink 3-4L water daily, avoid alcohol before hikes, consider Diamox if prone to altitude sickness.',
  },
  {
    q: 'Can I do this itinerary during rainy season?',
    a: 'Yes, with adjustments. Rainy season (May-Oct) brings afternoon showers but mornings are usually clear. Volcano hikes are trickier (muddy, cloudy). Hotels are cheaper and less crowded. Bring rain jacket and waterproof shoes. Some travelers prefer rainy season for the lush green landscapes.',
  },
  {
    q: 'What are the must-do activities in a 2 week Antigua itinerary?',
    a: 'Top 5: (1) Acatenango overnight hike, (2) Lake Atitlán day trip, (3) Pacaya volcano, (4) Chichicastenango market (Thu/Sun), (5) Colonial Antigua walking tour. If you only do these five, you\'ll have an incredible trip.',
  },
];

function DayCard({ data, index }: { data: DayData; index: number }) {
  const [isOpen, setIsOpen] = useState(index < 3);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full text-left cursor-pointer">
        <div className="flex items-center gap-4 p-5">
          <div className={`w-14 h-14 rounded-lg bg-gradient-to-br ${dayColors[index]} flex items-center justify-center flex-shrink-0`}>
            <i className={`${dayIcons[index]} text-white text-2xl`}></i>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-bold uppercase tracking-wider bg-gradient-to-r ${dayColors[index]} bg-clip-text text-transparent`}>
                Day {data.day}
              </span>
            </div>
            <h3 className="text-lg font-bold text-gray-900 truncate">{data.title}</h3>
            <p className="text-sm text-gray-500">{data.subtitle}</p>
          </div>
          <div className={`w-8 h-8 flex items-center justify-center text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
            <i className="ri-arrow-down-s-line text-xl"></i>
          </div>
        </div>
      </button>

      {isOpen && (
        <div className="px-5 pb-5 border-t border-gray-100">
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            <div className="bg-amber-50/60 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 flex items-center justify-center text-amber-600">
                  <i className="ri-sun-line text-lg"></i>
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-amber-700">Morning</span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{data.morning}</p>
            </div>
            <div className="bg-teal-50/60 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 flex items-center justify-center text-teal-600">
                  <i className="ri-sun-foggy-line text-lg"></i>
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-teal-700">Afternoon</span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{data.afternoon}</p>
            </div>
            <div className="bg-indigo-50/60 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 flex items-center justify-center text-indigo-600">
                  <i className="ri-moon-line text-lg"></i>
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-indigo-700">Evening</span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{data.evening}</p>
            </div>
          </div>

          <div className="mt-4 grid md:grid-cols-3 gap-3">
            <div className="flex items-start gap-2 text-sm">
              <div className="w-5 h-5 flex items-center justify-center text-gray-500 mt-0.5">
                <i className="ri-route-line"></i>
              </div>
              <div>
                <span className="font-semibold text-gray-900">Logistics:</span>
                <span className="text-gray-600 ml-1">{data.logistics}</span>
              </div>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <div className="w-5 h-5 flex items-center justify-center text-gray-500 mt-0.5">
                <i className="ri-money-dollar-circle-line"></i>
              </div>
              <div>
                <span className="font-semibold text-gray-900">Cost:</span>
                <span className="text-gray-600 ml-1">{data.cost}</span>
              </div>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <div className="w-5 h-5 flex items-center justify-center text-gray-500 mt-0.5">
                <i className="ri-rainy-line"></i>
              </div>
              <div>
                <span className="font-semibold text-gray-900">Alternative:</span>
                <span className="text-gray-600 ml-1">{data.alternative}</span>
              </div>
            </div>
          </div>

          {(data.proTip || data.link) && (
            <div className="mt-4 flex flex-wrap gap-3">
              {data.proTip && (
                <div className="flex-1 min-w-[250px] bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <div className="w-5 h-5 flex items-center justify-center text-amber-600 mt-0.5">
                    <i className="ri-lightbulb-line"></i>
                  </div>
                  <p className="text-sm text-amber-800">
                    <strong>Pro Tip:</strong> {data.proTip}
                  </p>
                </div>
              )}
              {data.link && (
                <Link
                  to={data.link.to}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-teal-600 hover:text-teal-700 cursor-pointer self-end"
                >
                  {data.link.text} <i className="ri-arrow-right-line"></i>
                </Link>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
/* -------------------------------------------------------------------------- */
/*                               Main Component                               */
/* -------------------------------------------------------------------------- */
export default function TwoWeekItineraryPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Itineraries', path: '/itineraries' },
    { label: '2 Week Itinerary', path: '/antigua-guatemala-2-week-itinerary' },
  ];

  const relatedGuides = [
    { title: 'Antigua Guatemala Travel Guide', path: '/antigua-guatemala-travel-guide', description: 'Complete planning resource for your Antigua trip' },
    { title: 'Things to Do in Antigua', path: '/things-to-do-in-antigua-guatemala', description: 'Top attractions and activities' },
    { title: 'Acatenango Volcano Hike', path: '/acatenango-hike-from-antigua', description: 'Complete guide to the overnight volcano trek' },
    { title: 'Day Trips from Antigua', path: '/day-trips-from-antigua-guatemala', description: 'Best excursions including Lake Atitlán and Pacaya' },
    { title: 'Airport Transfer Guide', path: '/guatemala-city-airport-to-antigua', description: 'How to get from Guatemala City Airport to Antigua' },
    { title: 'Best Hotels in Antigua', path: '/best-hotels-in-antigua-guatemala', description: 'Where to stay for 2 weeks' },
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: 'Antigua Guatemala 2 Week Itinerary: Your Complete 14-Day Guide',
        description:
          'Perfect 2 weeks in Antigua Guatemala itinerary with day trips, volcano hikes, budget tips, and logistics. Plan your 14-day Guatemala adventure.',
        image:
          'https://public.readdy.ai/ai/img_res/09c7f5d72ec641033bd9257604362956.jpg',
        author: { '@type': 'Organization', name: 'La Antigua Guatemala', url: 'https://la-antigua-guatemala.com' },
        publisher: {
          '@type': 'Organization',
          name: 'Antigua Guatemala Travel',
          url: 'https://la-antigua-guatemala.com',
          logo: { '@type': 'ImageObject', url: 'https://la-antigua-guatemala.com/logo.png' },
        },
        datePublished: '2025-01-15',
        dateModified: '2026-02-20',
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': 'https://la-antigua-guatemala.com/antigua-guatemala-2-week-itinerary',
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://la-antigua-guatemala.com' },
          { '@type': 'ListItem', position: 2, name: 'Itineraries', item: 'https://la-antigua-guatemala.com/itineraries' },
          { '@type': 'ListItem', position: 3, name: '2 Week Itinerary', item: 'https://la-antigua-guatemala.com/antigua-guatemala-2-week-itinerary' },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqData.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Antigua Guatemala 2 Week Itinerary: Complete 14-Day Guide"
        description="Perfect 2 weeks in Antigua Guatemala itinerary with day trips, volcano hikes, budget tips, and logistics. Plan your 14-day Guatemala adventure."
        keywords="antigua guatemala 2 week itinerary, 2 weeks in antigua guatemala, antigua guatemala itinerary 14 days, two week guatemala itinerary from antigua, day trips from antigua guatemala"
        canonical="https://la-antigua-guatemala.com/antigua-guatemala-2-week-itinerary"
        ogImage={{
          url: "https://readdy.ai/api/search-image?query=Antigua%20Guatemala%20panoramic%20aerial%20view%20at%20golden%20hour%20showing%20colorful%20colonial%20buildings%20cobblestone%20streets%20three%20volcanoes%20Agua%20Acatenango%20Fuego%20dramatic%20sunset%20sky%20travel%20destination%20photography%20warm%20tones&width=1200&height=630&seq=2weekhero001&orientation=landscape",
          width: 1200,
          height: 630
        }}
        structuredData={structuredData}
      />

      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[75vh] min-h-[600px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Antigua%20Guatemala%20panoramic%20aerial%20view%20at%20golden%20hour%20showing%20colorful%20colonial%20buildings%20cobblestone%20streets%20three%20volcanoes%20Agua%20Acatenango%20Fuego%20dramatic%20sunset%20sky%20travel%20destination%20photography%20warm%20tones&width=1920&height=1080&seq=2weekhero001&orientation=landscape"
            alt="Antigua Guatemala 2 week itinerary panoramic view with volcanoes"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        </div>

        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto w-full">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <i className="ri-calendar-check-line text-teal-300"></i>
            <span className="text-sm font-medium text-teal-100">14-Day Complete Itinerary</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">Antigua Guatemala 2 Week Itinerary</h1>
          <p className="text-lg md:text-xl mb-8 max-w-3xl mx-auto text-white/90">
            Your complete 14‑day guide to volcanoes, colonial streets, Lake Atitlán, coffee farms, and Mayan culture — all from one comfortable base.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/plan-my-antigua-trip"
              className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition whitespace-nowrap cursor-pointer"
            >
              Plan My Trip
            </Link>
            <a
              href="#day-by-day"
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold transition whitespace-nowrap cursor-pointer"
            >
              See Day-by-Day Plan
            </a>
          </div>
        </div>
      </section>

      {/* Breadcrumbs */}
      <Breadcrumbs items={breadcrumbItems} />

      {/* Quick Take Box */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                This <strong>antigua guatemala itinerary 14 days</strong> is designed for first‑time visitors who want to experience Guatemala’s highlights while using Antigua as a comfortable, safe base. Two weeks gives you the perfect balance: enough time to explore the colonial city deeply, tackle multiple volcano hikes, take day trips to Lake Atitlán and indigenous markets, and still have breathing room for spontaneous discoveries.
              </p>
              <p className="text-gray-600 mb-6">
                Whether you’re a couple, solo traveler, or simply someone who prefers a home base over constant moving, this <strong>two week guatemala itinerary from antigua</strong> covers everything from logistics to budget.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/antigua-guatemala-travel-guide" className="text-sm font-medium text-teal-600 hover:text-teal-700 flex items-center gap-1 cursor-pointer">
                  <i className="ri-book-open-line"></i> Full Travel Guide
                </Link>
                <Link to="/best-hotels-in-antigua-guatemala" className="text-sm font-medium text-teal-600 hover:text-teal-700 flex items-center gap-1 cursor-pointer">
                  <i className="ri-hotel-line"></i> Where to Stay
                </Link>
                <Link to="/guatemala-city-airport-to-antigua" className="text-sm font-medium text-teal-600 hover:text-teal-700 flex items-center gap-1 cursor-pointer">
                  <i className="ri-plane-line"></i> Airport Transfer
                </Link>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-100 rounded-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-500 text-white rounded-lg">
                  <i className="ri-flashlight-line text-lg"></i>
                </div>
                Quick Take
              </h2>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <i className="ri-user-line text-teal-500 mt-0.5"></i>
                  <span className="text-gray-700"><strong>For:</strong> First‑timers, couples, solo travelers</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-money-dollar-circle-line text-teal-500 mt-0.5"></i>
                  <span className="text-gray-700"><strong>Budget:</strong> $40/day (budget) to $150/day (comfort)</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-calendar-line text-teal-500 mt-0.5"></i>
                  <span className="text-gray-700"><strong>Best time:</strong> January – March (dry season)</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-map-pin-line text-teal-500 mt-0.5"></i>
                  <span className="text-gray-700"><strong>Base:</strong> Antigua (45 min from GUA airport)</span>
                </div>
                <div className="border-t border-teal-200 pt-3 mt-3">
                  <p className="font-semibold text-gray-900 mb-2">Highlights:</p>
                  <div className="flex flex-wrap gap-2">
                    {['Acatenango Hike', 'Lake Atitlán', 'Pacaya Volcano', 'Coffee Farms', 'Hobbitenango', 'Mayan Ruins', 'Colonial City', 'Cooking Class'].map((h) => (
                      <span key={h} className="bg-white px-2.5 py-1 rounded-full text-xs font-medium text-teal-700 border border-teal-200">{h}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Best Time & Where to Stay */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-100 text-amber-600 rounded-lg text-2xl mb-4">
                <i className="ri-sun-line"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Best Time to Visit</h3>
              <p className="text-sm text-gray-600 mb-3"><strong>Dry season (Nov‑Apr):</strong> Best weather for volcano hikes. Jan‑Mar are warmest and driest.</p>
              <p className="text-sm text-gray-600"><strong>Rainy season (May‑Oct):</strong> Afternoon showers, fewer tourists, better hotel deals.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 text-teal-600 rounded-lg text-2xl mb-4">
                <i className="ri-hotel-line"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Where to Stay 14 Days</h3>
              <p className="text-sm text-gray-600 mb-2"><strong>Budget:</strong> $15‑30/night — Selina, Tropicana Hostel</p>
              <p className="text-sm text-gray-600 mb-2"><strong>Mid‑range:</strong> $40‑80/night — Hotel Cirilo, Casa Encantada</p>
              <p className="text-sm text-gray-600"><strong>Comfort:</strong> $100‑200/night — Casa Santo Domingo</p>
              <Link to="/best-hotels-in-antigua-guatemala" className="text-sm font-medium text-teal-600 hover:text-teal-700 mt-3 inline-block cursor-pointer">
                See all hotels &rarr;
              </Link>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 text-emerald-600 rounded-lg text-2xl mb-4">
                <i className="ri-plane-line"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Getting to Antigua</h3>
              <p className="text-sm text-gray-600 mb-2"><strong>Shuttle:</strong> $12‑15/person, 45‑60 min, door‑to‑door</p>
              <p className="text-sm text-gray-600 mb-2"><strong>Private:</strong> $35‑50 for 1‑4 people</p>
              <p className="text-sm text-gray-600"><strong>Uber/taxi:</strong> $30‑40, less reliable</p>
              <Link to="/guatemala-city-airport-to-antigua" className="text-sm font-medium text-teal-600 hover:text-teal-700 mt-3 inline-block cursor-pointer">
                Full transfer guide &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Day‑by‑Day Itinerary */}
      <section id="day-by-day" className="py-20 bg-white scroll-mt-24">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Your 14‑Day Antigua Guatemala Itinerary</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Click each day to expand the full plan with morning, afternoon, evening suggestions, logistics, costs, and alternatives.
            </p>
          </div>

          <div className="space-y-4">
            {itineraryDays.map((day, i) => (
              <DayCard key={day.day} data={day} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Day Trips Comparison */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">Day Trips Comparison</h2>
          <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
            What to prioritize during your <strong>2 weeks in antigua guatemala</strong>
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { name: 'Acatenango Overnight', duration: '2 days', cost: '$35‑50', difficulty: 'Hard', icon: 'ri-tent-line', color: 'from-amber-500 to-orange-600', mustDo: true },
              { name: 'Lake Atitlán', duration: 'Full day', cost: '$40‑70', difficulty: 'Easy', icon: 'ri-ship-line', color: 'from-cyan-500 to-teal-600', mustDo: true },
              { name: 'Pacaya Volcano', duration: 'Half day', cost: '$25‑35', difficulty: 'Moderate', icon: 'ri-fire-line', color: 'from-rose-500 to-red-500', mustDo: true },
              { name: 'Coffee Farm', duration: 'Half day', cost: '$25‑40', difficulty: 'Easy', icon: 'ri-cup-line', color: 'from-lime-500 to-green-600', mustDo: false },
              { name: 'Chichicastenango', duration: 'Full day', cost: '$50‑80', difficulty: 'Easy', icon: 'ri-store-3-line', color: 'from-amber-400 to-yellow-500', mustDo: false },
              { name: 'Iximé Ruins', duration: 'Half day', cost: '$30‑50', difficulty: 'Easy', icon: 'ri-ancient-gate-line', color: 'from-emerald-400 to-teal-500', mustDo: false },
              { name: 'Monterrico Beach', duration: 'Full day', cost: '$40‑70', difficulty: 'Easy', icon: 'ri-sun-line', color: 'from-orange-400 to-amber-500', mustDo: false },
              { name: 'Hobbitenango', duration: 'Half day', cost: '$30‑50', difficulty: 'Easy', icon: 'ri-home-smile-line', color: 'from-teal-400 to-cyan-500', mustDo: false },
            ].map((trip) => (
              <div key={trip.name} className="bg-white rounded-lg p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${trip.color} flex items-center justify-center mb-3`}>
                  <i className={`${trip.icon} text-white text-lg`}></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{trip.name}</h3>
                <div className="space-y-1 text-sm text-gray-600">
                  <p><strong>Duration:</strong> {trip.duration}</p>
                  <p><strong>Cost:</strong> {trip.cost}</p>
                  <p><strong>Difficulty:</strong> {trip.difficulty}</p>
                </div>
                {trip.mustDo && (
                  <span className="inline-block mt-3 text-xs font-bold uppercase tracking-wider text-teal-600 bg-teal-50 px-2 py-1 rounded-full">Must‑Do</span>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3 max-w-2xl mx-auto">
            <div className="w-6 h-6 flex items-center justify-center text-amber-600 mt-0.5">
              <i className="ri-lightbulb-line text-lg"></i>
            </div>
            <p className="text-sm text-amber-800"><strong>Pro Tip:</strong> If you only have time for 3 day trips, do: Acatenango, Lake Atitlán, and Pacaya.</p>
          </div>
        </div>
      </section>

      {/* Budget Breakdown */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">Budget Breakdown: 2 Weeks in Antigua</h2>
          <p className="text-gray-600 text-center mb-10">Per day average: $50‑85 (budget) / $110‑190 (mid‑range) / $220‑395 (comfort)</p>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                level: 'Budget',
                total: '$720 – $1,180',
                color: 'from-emerald-500 to-green-600',
                items: [
                  { cat: 'Accommodation', val: '$210‑420' },
                  { cat: 'Food', val: '$210‑280' },
                  { cat: 'Activities', val: '$200‑300' },
                  { cat: 'Transport', val: '$50‑80' },
                  { cat: 'Misc', val: '$50‑100' },
                ],
              },
              {
                level: 'Mid‑Range',
                total: '$1,580 – $2,630',
                color: 'from-teal-500 to-emerald-600',
                items: [
                  { cat: 'Accommodation', val: '$560‑1,120' },
                  { cat: 'Food', val: '$420‑560' },
                  { cat: 'Activities', val: '$400‑600' },
                  { cat: 'Transport', val: '$100‑150' },
                  { cat: 'Misc', val: '$100‑200' },
                ],
              },
              {
                level: 'Comfort',
                total: '$3,100 – $5,520',
                color: 'from-amber-500 to-orange-600',
                items: [
                  { cat: 'Accommodation', val: '$1,400‑2,800' },
                  { cat: 'Food', val: '$700‑1,120' },
                  { cat: 'Activities', val: '$600‑900' },
                  { cat: 'Transport', val: '$200‑300' },
                  { cat: 'Misc', val: '$200‑400' },
                ],
              },
            ].map((b) => (
              <div key={b.level} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                <div className={`bg-gradient-to-r ${b.color} p-5 text-white`}>
                  <h3 className="text-lg font-bold">{b.level}</h3>
                  <p className="text-2xl font-bold mt-1">{b.total}</p>
                  <p className="text-sm text-white/80">14 days total</p>
                </div>
                <div className="p-5 space-y-3">
                  {b.items.map((item) => (
                    <div key={item.cat} className="flex justify-between text-sm">
                      <span className="text-gray-600">{item.cat}</span>
                      <span className="font-semibold text-gray-900">{item.val}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mid‑page CTA */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Antigua%20Guatemala%20Santa%20Catalina%20Arch%20at%20sunset%20with%20warm%20golden%20light%20cobblestone%20street%20Agua%20volcano%20in%20background%20romantic%20colonial%20atmosphere%20travel%20photography%20vibrant%20warm%20colors&width=1920&height=600&seq=2weekcta001&orientation=landscape"
            alt="Santa Catalina Arch Antigua Guatemala"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Plan Your 2 Week Antigua Trip?</h2>
          <p className="text-lg text-white/90 mb-8">Get personalized help planning your 14‑day Antigua adventure with custom recommendations.</p>
          <Link
            to="/plan-my-antigua-trip"
            className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap cursor-pointer"
          >
            Plan My Trip &rarr;
          </Link>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-10 text-center">Common Mistakes to Avoid</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { icon: 'ri-calendar-todo-line', title: 'Overpacking the schedule', desc: 'Two weeks sounds long, but volcano hikes are exhausting. Build in 2‑3 rest days.' },
              { icon: 'ri-shield-cross-line', title: 'Skipping travel insurance', desc: 'Medical evacuation from Acatenango costs $10,000+. Get coverage with adventure sports included.' },
              { icon: 'ri-lungs-line', title: 'Not acclimatizing', desc: 'Spend 2‑3 days in Antigua (1,500 m) before hiking to 3,976 m on Acatenango.' },
              { icon: 'ri-calendar-close-line', title: 'Booking tours too far ahead', desc: 'Weather changes. Book 1‑2 days out for flexibility.' },
              { icon: 'ri-drop-line', title: 'Ignoring altitude sickness', desc: 'Drink 3‑4 L water daily. Avoid alcohol before hikes, consider Diamox if prone to altitude sickness.' },
              { icon: 'ri-money-dollar-box-line', title: 'Carrying too much cash', desc: 'ATMs are plentiful in Antigua. Carry $50‑100 max. Use cards where accepted.' },
              { icon: 'ri-store-line', title: 'Missing market days', desc: 'Chichicastenango is only Thu/Sun. Check dates before planning.' },
              { icon: 'ri-translate', title: 'Not learning basic Spanish', desc: '“¿Cuánto cuesta?” and “Gracias” go a long way outside tourist areas.' },
            ].map((m, i) => (
              <div key={i} className="flex items-start gap-4 bg-red-50/50 border border-red-100 rounded-lg p-4">
                <div className="w-10 h-10 flex items-center justify-center bg-red-100 text-red-500 rounded-lg flex-shrink-0">
                  <i className={`${m.icon} text-xl`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm mb-1">{m.title}</h3>
                  <p className="text-sm text-gray-600">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Add‑On Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Optional Add‑On: 3‑5 Days Outside Antigua
          </h2>
          <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
            If you want to extend beyond 2 weeks or swap some Antigua days for other destinations
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Lake Atitlán */}
            <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div className="relative h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Lake%20Atitlan%20Guatemala%20stunning%20panoramic%20view%20with%20three%20volcanoes%20surrounding%20deep%20blue%20lake%20indigenous%20villages%20on%20shoreline%20traditional%20boats%20colorful%20lakeside%20town%20travel%20photography&width=600&height=400&seq=addon-atitlan001&orientation=landscape"
                  alt="Lake Atitlán Guatemala"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-3 left-3 bg-teal-500 text-white text-xs font-bold px-3 py-1 rounded-full">3‑4 DAYS</div>
              </div>
              <div className="p-5">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Lake Atitlán Extended Stay</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Village hopping, kayaking, Indian Nose sunrise hike, weaving workshops. Base in San Marcos, San Pedro, or Panajachel.
                </p>
                <p className="text-sm text-gray-700"><strong>Cost:</strong> Similar to Antigua. Shuttle $10‑15.</p>
              </div>
            </div>

            {/* Flores & Tikal */}
            <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div className="relative h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Tikal%20ruins%20Guatemala%20ancient%20Mayan%20temple%20pyramids%20emerging%20from%20dense%20jungle%20canopy%20misty%20morning%20atmospheric%20archaeological%20site%20travel%20photography%20dramatic%20lighting&width=600&height=400&seq=addon-tikal001&orientation=landscape"
                  alt="Tikal ruins Guatemala"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-3 left-3 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">3‑4 DAYS</div>
              </div>
              <div className="p-5">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Flores & Tikal</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Fly from Guatemala City ($80‑150 RT). Tikal ruins, Yaxhá ruins, Ixpanpajul canopy tours, Flores island town.
                </p>
                <p className="text-sm text-gray-700"><strong>Cost:</strong> $300‑600 for 3 days.</p>
              </div>
            </div>

            {/* Semuc Champey */}
            <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div className="relative h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Semuc%20Champey%20Guatemala%20turquoise%20limestone%20natural%20pools%20cascading%20through%20lush%20tropical%20jungle%20crystal%20clear%20water%20surrounded%20by%20dense%20green%20vegetation%20travel%20photography&width=600&height=400&seq=addon-semuc001&orientation=landscape"
                  alt="Semuc Champey Guatemala"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-3 left-3 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">2‑3 DAYS</div>
              </div>
              <div className="p-5">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Semuc Champey</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Turquoise limestone pools in the jungle. Kan’ba caves, river tubing, waterfall rappelling. Adventurous but worth it.
                </p>
                <p className="text-sm text-gray-700"><strong>Cost:</strong> $150‑300 for 2‑3 days.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Safety & Packing */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Safety */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-100 text-teal-600 rounded-lg">
                  <i className="ri-shield-check-line text-xl"></i>
                </div>
                Safety Tips for 2 Weeks
              </h2>
              <div className="space-y-3">
                {[
                  'Walk in groups after dark, especially to/from Cerro de la Cruz',
                  'Don’t flash expensive cameras or jewelry',
                  'Use ATMs inside banks during daytime',
                  'Only use licensed volcano tour operators',
                  'Keep copies of passport separate from originals',
                  'POLITUR (tourist police) patrol main areas daily',
                  'Drink bottled or filtered water only',
                  'Get travel insurance with medical evacuation coverage',
                ].map((tip, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-5 h-5 flex items-center justify-center text-teal-500 mt-0.5">
                      <i className="ri-check-line"></i>
                    </div>
                    <p className="text-sm text-gray-700">{tip}</p>
                  </div>
                ))}
                <Link to="/is-antigua-guatemala-safe" className="text-sm font-medium text-teal-600 hover:text-teal-700 mt-3 inline-block cursor-pointer">
                  Full safety guide &rarr;
                </Link>
              </div>
            </div>

            {/* Packing */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-100 text-amber-600 rounded-lg">
                  <i className="ri-luggage-cart-line text-xl"></i>
                </div>
                Packing Essentials
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">Clothing</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• 7 days of clothes (laundry $3‑5)</li>
                    <li>• Layers: T‑shirts, fleece, rain jacket</li>
                    <li>• Hiking boots + walking shoes</li>
                    <li>• Swimsuit, hat, sunglasses</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">Gear</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Daypack (20‑30 L)</li>
                    <li>• Headlamp (for Acatenango)</li>
                    <li>• Reusable water bottle</li>
                    <li>• Power bank + adapters</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">Health</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Sunscreen SPF 50+</li>
                    <li>• Insect repellent (DEET 30%+)</li>
                    <li>• Altitude meds (Diamox)</li>
                    <li>• Basic first‑aid kit</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">Documents</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Passport (valid 6+ months)</li>
                    <li>• Travel insurance card</li>
                    <li>• Credit/debit cards</li>
                    <li>• $200‑300 USD cash</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-10 text-center">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faqData.map((faq, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                >
                  <h3 className="font-bold text-gray-900 text-sm md:text-base pr-4">{faq.q}</h3>
                  <div
                    className={`w-8 h-8 flex items-center justify-center text-gray-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}
                  >
                    <i className="ri-arrow-down-s-line text-xl"></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 border-t border-gray-100">
                    <p className="text-sm text-gray-700 leading-relaxed pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* End CTA */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-emerald-700">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Start Planning Your 14‑Day Adventure</h2>
          <p className="text-lg text-teal-100 mb-8 max-w-2xl mx-auto">
            Turn this itinerary into reality. Get personalized help or download our free PDF guide.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/plan-my-antigua-trip"
              className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-teal-50 transition whitespace-nowrap cursor-pointer"
            >
              Plan My Trip &rarr;
            </Link>
            <Link
              to="/antigua-guatemala-3-day-itinerary"
              className="inline-block bg-white hover:bg-gray-100 text-teal-500 px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap cursor-pointer"
            >
              Download Free Itinerary
            </Link>
          </div>
        </div>
      </section>

      {/* Last Updated + Related */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-sm text-gray-500 text-center mb-8">
            <em>
              This <strong>antigua guatemala 2 week itinerary</strong> was last updated February 20, 2026. For custom planning help, visit our{' '}
              <Link to="/plan-my-antigua-trip" className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer">
                Plan My Trip
              </Link>{' '}
              page.
            </em>
          </p>
        </div>
      </section>

      <RelatedGuides guides={relatedGuides} />

      <Footer />
    </div>
  );
}
